import { useState, useEffect } from 'react';
import { Button, Collapse, Row, Col, Form, InputGroup, Badge } from 'react-bootstrap';
import cx from 'classnames';
import CompareIcon from '../../../CompareIcon';
import CopyToClipboardIcon from 'components/CopyToClipboardIcon';
import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import { Grossist } from 'services/KjederegisteretAdminApi/ChangeRequestApi/types';
import {
  CompareStatus,
  qaExistInCommaSeparatedString,
} from 'pages/QA/components/QACardDetails/utils';
import Style from '../../index.module.css';
import { EnhetViewModel } from 'services/KjederegisteretAdminApi/EnhetApi/types';
import EnhetApi from 'services/KjederegisteretAdminApi/EnhetApi';
import openInNewTab from 'utils/functions/openInNewTab';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import { ChangeRequestValidator } from 'pages/QA/components/QACardDetails/ChangeRequestValidator';

interface Props {
  changeRequest: {
    grossistKundenummer?: string;
    grossistKundenummerHos?: string;
    grossistKundenummerHosGln?: string;
  };
  error?: string;
  hovedGrossist?: Grossist;
  andreGrossister?: Grossist[];
  onChange: (updatedRequest: Grossist, updateHovedGrossist: boolean) => void;
  enhetId?: number;
}

const GrossistKundenummerRow = (props: Props) => {
  const [isExpandRow, setIsExpandRow] = useState<boolean>(false);
  const [otherConnectedEnheter, setOtherConnectedEnheter] = useState<EnhetViewModel[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const isMountedRef = useIsMountedRef();
  const [compareStatus, setCompareStatus] = useState<CompareStatus>(
    props.changeRequest.grossistKundenummerHosGln
      ? !props.hovedGrossist?.kundenummerHosGrossist
        ? 'ulik'
        : qaExistInCommaSeparatedString(
            [
              props.andreGrossister
                ?.filter((x) => x !== undefined)
                .map((x) => x.kundenummerHosGrossist + '-' + x.grossistGln),
              props.hovedGrossist?.kundenummerHosGrossist + '-' + props.hovedGrossist?.grossistGln,
            ].join(','),
            props.changeRequest.grossistKundenummer +
              '-' +
              props.changeRequest.grossistKundenummerHosGln,
          )
      : 'ignorer',
  );
  const [crValueError, setCrValueError] = useState<string | null>(null);

  const handleGrossistInfoUpdated = (
    grossistKundenummer: string,
    grossistGln: string,
    enhetNavn: string,
  ) => {
    props.onChange(
      {
        kundenummerHosGrossist: grossistKundenummer,
        grossistGln: grossistGln,
        grossistEnhetNavn: enhetNavn,
      },
      !props.hovedGrossist?.kundenummerHosGrossist,
    );
  };

  useEffect(() => {
    if (
      compareStatus === 'ulik' ||
      compareStatus === 'ny' ||
      compareStatus === 'ignorer' ||
      otherConnectedEnheter?.length > 0
    ) {
      setIsExpandRow(true);
    }
  }, [compareStatus, otherConnectedEnheter]);

  useEffect(() => {
    setCompareStatus(
      props.changeRequest.grossistKundenummerHosGln
        ? !props.hovedGrossist?.kundenummerHosGrossist
          ? 'ulik'
          : qaExistInCommaSeparatedString(
              [
                props.andreGrossister
                  ?.filter((x) => x !== undefined)
                  .map((x) => x.kundenummerHosGrossist + '-' + x.grossistGln),
                props.hovedGrossist?.kundenummerHosGrossist +
                  '-' +
                  props.hovedGrossist?.grossistGln,
              ].join(','),
              props.changeRequest.grossistKundenummer +
                '-' +
                props.changeRequest.grossistKundenummerHosGln,
            )
        : 'ignorer',
    );
  }, [
    props.changeRequest.grossistKundenummer,
    props.hovedGrossist?.kundenummerHosGrossist,
    props.andreGrossister,
  ]);

  const loadOtherConnectedEnheter = async () => {
    if (props.changeRequest.grossistKundenummerHosGln && props.changeRequest.grossistKundenummer) {
      setLoading(true);
      const result = await EnhetApi.getByGrossistkundenr(
        props.changeRequest.grossistKundenummerHosGln,
        props.changeRequest.grossistKundenummer,
      );
      if (isMountedRef.current) {
        setOtherConnectedEnheter(
          result?.length > 0 ? result.filter((x) => x.id !== props.enhetId) : [],
        );
      }
      setLoading(false);
    } else {
      // When grossistKundenummerHosGln is empty, clear the otherConnectedEnheter state
      setOtherConnectedEnheter([]);
    }
  };

  useEffect(() => {
    loadOtherConnectedEnheter();
  }, [props.changeRequest.grossistKundenummerHosGln, props.changeRequest.grossistKundenummer]);

  // Check if the value from the change request has any errors
  useEffect(() => {
    if (props.changeRequest.grossistKundenummer && props.changeRequest.grossistKundenummerHosGln) {
      const error = ChangeRequestValidator.hasGrossistKundenummerErrors(
        props.changeRequest.grossistKundenummer,
        props.changeRequest.grossistKundenummerHosGln,
      );
      setCrValueError(error);
    } else {
      setCrValueError(null);
    }
  }, [props.changeRequest.grossistKundenummer, props.changeRequest.grossistKundenummerHosGln]);

  return (
    <>
      <div
        className={cx(Style.changeRequestRowHeaderContainer, {
          [Style.isNotExpandedRowContainer]: !isExpandRow,
        })}
        onClick={() => setIsExpandRow(!isExpandRow)}
      >
        <h6 className={cx(Style.changeRequestRowHeader, Style.changeRequestRowHeaderGrid)}>
          <Row className="g-0">
            <Col xs={5} className="d-flex align-items-center gap-1">
              {!isExpandRow && <CompareIcon compareStatus={compareStatus} size="sm" />}
              <span className={Style.changeRequestRowHeaderText}>
                Kundenummer hos grossist:
                <strong>
                  {' '}
                  {props.changeRequest.grossistKundenummerHos}{' '}
                  {props.changeRequest.grossistKundenummerHosGln &&
                    `(${props.changeRequest.grossistKundenummerHosGln})`}
                </strong>
              </span>
            </Col>
            <Col xs={2}></Col>
            <Col xs={4} className="d-flex align-items-center" style={{ minWidth: 0 }}>
              {isExpandRow && props.hovedGrossist?.grossistGln && (
                <span style={{ display: 'inline-block', minWidth: 0 }}>
                  {props.hovedGrossist?.grossistEnhetNavn} <Badge>Hovedgrossist</Badge>
                </span>
              )}
            </Col>
            <Col xs={1} className="d-flex align-items-center justify-content-end">
              <Button variant="link" size="sm">
                <IcebergIcon icon={isExpandRow ? 'chevron-up' : 'chevron-down'} bold />
              </Button>
            </Col>
          </Row>
        </h6>
      </div>
      <Collapse in={isExpandRow}>
        <div>
          <Row className={Style.formGroupRow}>
            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm" className={Style.inputGroup}>
                  <Form.Control
                    value={props.changeRequest.grossistKundenummer || ''}
                    readOnly
                    isValid={!crValueError && !!props.changeRequest.grossistKundenummer}
                    isInvalid={!!crValueError}
                  />
                  <InputGroup.Text>
                    <CopyToClipboardIcon val={props.changeRequest.grossistKundenummer} />
                  </InputGroup.Text>
                  <Form.Control.Feedback type="invalid">
                    {crValueError ?? 'Kundenummer er ugyldig.'}
                  </Form.Control.Feedback>
                </InputGroup>
                {!loading && otherConnectedEnheter?.length > 0 && (
                  <Form.Text>
                    <span style={{ color: 'red' }}>
                      Andre enheter koblet til dette kundenummeret:{' '}
                    </span>
                    {!loading &&
                      otherConnectedEnheter.map((enhet, index, array) => (
                        <span key={enhet.id}>
                          <a
                            target="_blank"
                            href="#"
                            onClick={() => openInNewTab(`/enheter/${enhet.id}`)}
                          >
                            {enhet.enhetnavn}: {enhet.lopenr}
                          </a>
                          {index < array.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>

            <Col xs="auto" md={2} className={Style.compareIconContainer}>
              {!!crValueError ? (
                <CompareIcon compareStatus="ugyldig" tooltipText={crValueError} />
              ) : (
                <CompareIcon
                  compareStatus={compareStatus}
                  onClick={() =>
                    handleGrossistInfoUpdated(
                      props.changeRequest.grossistKundenummer,
                      props.changeRequest.grossistKundenummerHosGln,
                      props.changeRequest.grossistKundenummerHos,
                    )
                  }
                />
              )}
            </Col>

            <Col xs={12} md={5}>
              <Form.Group className={Style.formGroup}>
                <InputGroup size="sm">
                  <Form.Control
                    value={props.hovedGrossist?.kundenummerHosGrossist || ''}
                    readOnly
                    isInvalid={!!props.error}
                  />
                </InputGroup>
                <Form.Text>
                  {props.andreGrossister?.length > 0 &&
                    props.andreGrossister
                      .filter((x) => x.grossistGln !== undefined && x.grossistGln !== null)
                      .map((grossist) => (
                        <span key={grossist.grossistGln} style={{ display: 'inline-block' }}>
                          {grossist?.grossistEnhetNavn}{' '}
                          {grossist?.kundenummerHosGrossist &&
                            `(${grossist?.kundenummerHosGrossist})`}
                        </span>
                      ))}
                </Form.Text>
                <Form.Control.Feedback type="invalid">{props.error}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </div>
      </Collapse>
    </>
  );
};

export default GrossistKundenummerRow;
